import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ETAPAS = ["aberta", "aguardando_analise", "peca_solicitada", "aguardando_peca", "peca_recebida", "peca_disponivel", "aguardando_retorno", "em_execucao", "concluida"];
const TRANS = { aberta: ["aguardando_analise", "peca_solicitada", "aguardando_retorno"], aguardando_analise: ["peca_solicitada", "aguardando_retorno"],
  peca_solicitada: ["aguardando_peca", "peca_recebida"], aguardando_peca: ["peca_recebida"], peca_recebida: ["peca_disponivel"],
  peca_disponivel: ["aguardando_retorno"], aguardando_retorno: ["em_execucao"], em_execucao: ["concluida"], concluida: [] };
const ETAPA_ROT = { aberta: "Aberta", aguardando_analise: "Aguardando análise", peca_solicitada: "Peça solicitada", aguardando_peca: "Aguardando peça",
  peca_recebida: "Peça recebida", peca_disponivel: "Peça disponível (libera retorno)", aguardando_retorno: "Aguardando retorno técnico",
  em_execucao: "Em execução", concluida: "Concluída" };

// AST-02 — controlador de etapas do chamado (sem novos status globais de OS)
function EtapaControl({ a, onSaved }) {
  const [obs, setObs] = useState("");
  const [prev, setPrev] = useState("");
  const atual = a.etapa || "aberta";
  const proximas = TRANS[atual] || [];
  const avancar = async (etapa) => {
    try {
      const r = await api.post(`/assistencias/${a.id}/etapa`, { etapa, observacao: obs || null, previsao: prev || null });
      toast.success(`Etapa: ${ETAPA_ROT[r.data.etapa] || r.data.etapa}${r.data.retorno ? ` · retorno técnico: ${r.data.retorno}` : ""}`);
      setObs(""); setPrev(""); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-border bg-[#FAFAFA] p-3 mb-3" data-testid={`assist-etapas-${a.id}`}>
      <div className="flex flex-wrap gap-1 mb-2">
        {ETAPAS.map((e) => (
          <span key={e} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${e === atual ? "brand-bg text-white border-transparent" : ETAPAS.indexOf(e) < ETAPAS.indexOf(atual) ? "bg-[#E4E4E7] border-transparent" : "border-border text-muted-foreground"}`}
            data-testid={`etapa-chip-${a.id}-${e}`}>{ETAPA_ROT[e]}</span>
        ))}
      </div>
      {a.servico_filho_id && <div className="text-[11px] text-muted-foreground mb-2">OS filha: <span className="font-mono">{a.pim_code_filho || a.servico_filho_id.slice(0, 8)}</span> · OS mãe: <span className="font-mono">{a.pim_code}</span>{a.avaliacao_pos_montagem_id && " · via Avaliação Pós-Montagem"}</div>}
      {proximas.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Observação (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} className="text-sm w-56" data-testid={`etapa-obs-${a.id}`} />
          {(atual === "aberta" || atual === "aguardando_analise" || atual === "peca_solicitada") && (
            <Input type="date" value={prev} onChange={(e) => setPrev(e.target.value)} className="text-sm w-40" data-testid={`etapa-previsao-${a.id}`} />
          )}
          {proximas.map((p) => (
            <Button key={p} variant="outline" className="btn-sharp text-xs" onClick={() => avancar(p)} data-testid={`etapa-btn-${a.id}-${p}`}>
              → {p === "aguardando_retorno" && ["aberta", "aguardando_analise"].includes(atual) ? "Liberar retorno técnico (sem peça)" : ETAPA_ROT[p]}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Assistencias() {
  const [list, setList] = useState([]);
  const [particulares, setParticulares] = useState([]);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ aprovado: true, previsao_entrega: "", observacao: "" });
  const [novoEvento, setNovoEvento] = useState("");

  const load = () => {
    api.get("/assistencias").then(r => setList(r.data));
    api.get("/particular/assistencias").then(r => setParticulares(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const validar = async () => {
    try {
      const fd = new FormData();
      fd.append("aprovado", String(form.aprovado));
      if (form.previsao_entrega) fd.append("previsao_entrega", form.previsao_entrega);
      if (form.observacao) fd.append("observacao", form.observacao);
      await api.post(`/assistencias/${open.id}/validar`, fd);
      toast.success("Validação registrada");
      setOpen(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addEvento = async (aid) => {
    if (!novoEvento) return;
    try {
      const fd = new FormData();
      fd.append("evento", novoEvento);
      await api.post(`/assistencias/${aid}/timeline`, fd);
      toast.success("Evento registrado"); setNovoEvento(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="label-cap">Pós-venda</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Assistências</h1>
        </div>
        <Button variant="outline" className="btn-sharp"
          onClick={() => api.post("/jobs/sla-assistencia").then((r) => { toast.success(`${r.data.eventos_disparados} eventos disparados`); load(); })}
          data-testid="btn-sla-check">
          Verificar SLA
        </Button>
      </div>

      {particulares.length > 0 && (
        <div className="border border-border bg-white p-5 mb-4" data-testid="assist-particulares">
          <h2 className="font-heading font-bold mb-1">Assistências — Cliente Particular ({particulares.length})</h2>
          <p className="text-xs text-muted-foreground mb-3">Solicitadas pelo cliente no Portal do Pedido, com fotos e vídeo anexados.</p>
          <div className="grid lg:grid-cols-2 gap-3">
            {particulares.map(a => {
              const horas = Math.floor((new Date() - new Date(a.created_at)) / 3600000);
              const restam = Math.max(0, 72 - horas);
              const aberta = a.status === "aguardando_analise_assistencia";
              return (
              <div key={a.id} className="border border-border p-4" data-testid={`assist-cp-${a.pim_code}`}>
                <div className="flex justify-between mb-1">
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{a.pim_code} · {a.protocolo_cp}</div>
                    <div className="font-heading font-bold">{a.cliente_nome}</div>
                    <div className="text-xs text-muted-foreground">{a.cliente_cidade}/{a.cliente_estado}</div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] uppercase font-bold tracking-wider">{(a.status || "").replace(/_/g, " ")}</span>
                    {aberta && (
                      <div className={`text-xs font-mono font-bold mt-1 ${restam <= 24 ? "text-[#E60000]" : "text-muted-foreground"}`}
                        data-testid={`assist-cp-sla-${a.pim_code}`}>
                        {restam > 0 ? `⏱ ${restam}h p/ responder (72h)` : "🔴 SLA 72h vencido"}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-sm mt-1">{a.descricao_assistencia}</p>
                {a.anexos?.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid={`assist-cp-anexos-${a.pim_code}`}>
                    {a.anexos.map((x, i) => (
                      (x.content_type || "").startsWith("video/") ? (
                        <video key={i} src={x.url} controls className="h-24 rounded border border-border" data-testid={`assist-cp-video-${i}`} />
                      ) : (
                        <a key={i} href={x.url} target="_blank" rel="noreferrer">
                          <img src={x.url} alt="anexo do cliente" className="h-24 w-24 object-cover rounded border border-border hover:opacity-80" data-testid={`assist-cp-foto-${i}`} />
                        </a>
                      )
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-2">Sem anexos do cliente.</p>
                )}
              </div>
            );})}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {list.length === 0 && <div className="col-span-2 p-12 text-center text-muted-foreground border border-border bg-white">Nenhuma assistência aberta</div>}
        {list.map((a) => {
          const dias = Math.floor((new Date() - new Date(a.data_abertura)) / 86400000);
          const danger = dias >= 20;
          return (
            <div key={a.id} className={`border ${danger ? "border-[#E60000]" : "border-border"} bg-white p-5`}
              data-testid={`assist-${a.id}`}>
              <div className="flex justify-between mb-3">
                <div>
                  <div className="font-mono text-xs text-muted-foreground">{a.pim_code}</div>
                  <div className="font-heading font-bold mt-1">{a.cliente_nome}</div>
                </div>
                <div className="text-right">
                  <div className={`text-xs uppercase tracking-wider ${danger ? "text-[#E60000]" : ""}`}>{a.status}</div>
                  <div className="font-mono text-xs">{dias}d aberto</div>
                </div>
              </div>
              <div className="text-sm text-muted-foreground mb-3">{a.descricao}</div>
              <EtapaControl a={a} onSaved={load} />
              <div className="border-t border-border pt-3 space-y-2">
                <div className="label-cap">Timeline</div>
                {(a.timeline || []).slice(-5).map((t, i) => (
                  <div key={i} className="text-xs border-l-2 border-border pl-2">
                    <div className="font-mono text-[10px] text-muted-foreground">{new Date(t.at).toLocaleString("pt-BR")}</div>
                    <div>{t.evento}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Input placeholder="Novo evento" value={open?.id === a.id ? novoEvento : ""}
                  onChange={(e) => setNovoEvento(e.target.value)} onFocus={() => setOpen({ id: a.id, mode: "evento" })}
                  className="text-sm" data-testid={`assist-evento-${a.id}`} />
                <Button variant="outline" className="btn-sharp" onClick={() => addEvento(a.id)} data-testid={`assist-add-evento-${a.id}`}>
                  Add
                </Button>
                {a.status === "aguardando_validacao_montadora" && (
                  <Button className="btn-sharp brand-bg text-white"
                    onClick={() => { setOpen({ ...a, mode: "validar" }); setForm({ aprovado: true, previsao_entrega: "", observacao: "" }); }}
                    data-testid={`assist-validar-${a.id}`}>
                    Validar
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open?.mode === "validar"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Validar assistência</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <button className={`btn-sharp px-4 py-2 ${form.aprovado ? "brand-bg text-white" : "border-border"}`} onClick={() => setForm(f => ({...f, aprovado: true}))} data-testid="val-aprovar">Aprovar</button>
              <button className={`btn-sharp px-4 py-2 ${!form.aprovado ? "bg-[#E60000] text-white" : "border-border"}`} onClick={() => setForm(f => ({...f, aprovado: false}))} data-testid="val-negar">Negar</button>
            </div>
            {form.aprovado && (
              <div>
                <Label className="label-cap">Previsão de entrega (data)</Label>
                <Input type="date" value={form.previsao_entrega} onChange={(e) => setForm(f => ({...f, previsao_entrega: e.target.value}))} className="mt-2" data-testid="val-data" />
              </div>
            )}
            <div>
              <Label className="label-cap">Observação</Label>
              <Textarea value={form.observacao} onChange={(e) => setForm(f => ({...f, observacao: e.target.value}))} className="mt-2" data-testid="val-obs" />
            </div>
            <Button onClick={validar} className="w-full btn-sharp brand-bg text-white" data-testid="val-confirmar">Confirmar validação</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
